import * as Sentry from '@sentry/browser';

function isProbablyBlockedByAdblockUrl(url) {
  const adblockPattern = /adblock/;
  const googleAnalyticsPattern = /https?:\/\/(www\.)?google-analytics\.com\/(collect(\?.*)?|analytics\.js|gtag\/js|gtm\.js(\?.*)?|fbevents\.js|ga4-[\w-]+\.js)/;
  const batPattern = /bat\.js/;

  return adblockPattern.test(url) || googleAnalyticsPattern.test(url) || batPattern.test(url);
}

function isProbablyBlockedByAdblockBreadcrumb(breadcrumb) {
  if (
    breadcrumb.level !== 'error' ||
    (breadcrumb.category !== 'xhr' && breadcrumb.category !== 'fetch')
  ) {
    return false;
  }

  const url = breadcrumb.data?.url;
  if (!url) {
    return false;
  }

  return isProbablyBlockedByAdblockUrl(url);
}

function isProbablyBlockedByAdblockNetworkError(error) {
  const now = Date.now();

  if (!error.breadcrumbs) {
    return false;
  }

  // We go from the back since the last breadcrumb is most likely the erroneous one
  for (let i = error.breadcrumbs.length - 1; i >= 0; i--) {
    const breadcrumb = error.breadcrumbs[i];
    if (!breadcrumb) {
      continue;
    }

    // We only need to check the last 5s of breadcrumbs as any earlier breadcrumbs are definitely unrelated
    if (breadcrumb.timestamp && now - breadcrumb.timestamp * 1000 > 5000) {
      break;
    }

    if (isProbablyBlockedByAdblockBreadcrumb(breadcrumb)) {
      return true;
    }
  }

  return false;
}

function isFacebookInAppBrowserError(error) {
  // Check error message for Facebook-specific issues
  const errorMessage = error.exception?.values?.[0]?.value || error.message || '';
  const facebookErrorPatterns = [
    /Java object is gone/,
    /FB_IAB/,
    /FBAV/,
    /window\.webkit\.messageHandlers/,
    /undefined is not an object \(evaluating 'window\.webkit\.messageHandlers/,
    /Can't find variable: _AutofillCallbackHandler/,
    /Can't find variable: gmo/,
    // fbq is the Meta Pixel SDK — blocked by ad blockers, GTM still tries to call it
    /Can't find variable: fbq/,
    /fbq is not defined/,
    /_AutofillCallbackHandler/,
  ];

  if (facebookErrorPatterns.some(pattern => pattern.test(errorMessage))) {
    return true;
  }

  // Check user agent for Facebook browser
  const userAgent = error.request?.headers?.['User-Agent'] || 
                   error.contexts?.browser?.name || 
                   navigator?.userAgent || '';
  
  if (/Facebook|FB_IAB|FBAV/i.test(userAgent)) {
    return true;
  }

  // Check referrer for Facebook
  const referrer = error.request?.headers?.Referer || 
                  error.contexts?.page?.referrer || 
                  document?.referrer || '';
  
  if (/facebook\.com|m\.facebook\.com/i.test(referrer)) {
    return true;
  }

  // Check URL parameters for Facebook traffic
  const url = error.contexts?.page?.url || window?.location?.href || '';
  if (/[?&](fbclid|utm_source=fb)/i.test(url)) {
    return true;
  }

  return false;
}

function isWebViewError(error) {
  const errorMessage = error.exception?.values?.[0]?.value || error.message || '';
  const webViewErrorPatterns = [
    /WebView/i,
    /Java object is gone/,
    /Non-Error promise rejection captured/,
    /Script error/,
    /Network request failed/,
    /Load failed/,
    /The operation couldn't be completed/,
    /window\.webkit\.messageHandlers/,
    /undefined is not an object \(evaluating 'window\.webkit\.messageHandlers/,
    /Can't find variable: _AutofillCallbackHandler/,
    /Can't find variable: gmo/,
    /Can't find variable: zaloJSV2/,
    /_AutofillCallbackHandler/,
  ];

  if (webViewErrorPatterns.some(pattern => pattern.test(errorMessage))) {
    return true;
  }

  // SyntaxErrors originating from a WebView (e.g. host app injecting scripts that corrupt
  // the JS environment) should be ignored — Chrome 127+ would never produce these from our code
  const browserName = error.contexts?.browser?.name || '';
  if (/WebView/i.test(browserName) && /SyntaxError/i.test(errorMessage)) {
    return true;
  }

  return false;
}

function isThirdPartyAxiosError(hint) {
  const originalException = hint && hint.originalException;
  if (!originalException || typeof originalException !== 'object') {
    return false;
  }

  const isAxiosNetworkError = originalException.name === 'AxiosError' && originalException.code === 'ERR_NETWORK';
  if (!isAxiosNetworkError || !originalException.config) {
    return false;
  }

  const url = (originalException.config.baseURL || '') + (originalException.config.url || '');
  return url && !url.includes('laundryheap.com');
}

function isAnonymousCallStackExceeded(event) {
  if (!event.exception || !event.exception.values || event.exception.values.length === 0) {
    return false;
  }

  const exception = event.exception.values[0];
  if (exception.type !== 'RangeError' || exception.value !== 'Maximum call stack size exceeded') {
    return false;
  }

  const frames = exception.stacktrace && exception.stacktrace.frames;
  if (!frames || frames.length === 0) {
    return false;
  }

  return frames.every(frame => !frame.filename || frame.filename === '<anonymous>');
}

function isAnonymousClickError(event) {
  if (!event.exception || !event.exception.values || event.exception.values.length === 0) {
    return false;
  }

  const exception = event.exception.values[0];
  if (!exception.value || !exception.value.includes("Cannot read properties of null (reading 'click')")) {
    return false;
  }

  const frames = exception.stacktrace && exception.stacktrace.frames;
  if (!frames || frames.length === 0) {
    return true; // No stack trace means it is an obfuscated external script
  }

  // Ensure all frames are anonymous or browser extensions
  return frames.every(frame => !frame.filename || frame.filename === '<anonymous>' || frame.filename.startsWith('chrome-extension://') || frame.filename.startsWith('moz-extension://'));
}

function isThirdPartyBrowserError(error) {
  // Check for Facebook in-app browser errors
  if (isFacebookInAppBrowserError(error)) {
    return true;
  }

  // Check for other third-party browser/WebView errors
  if (isWebViewError(error)) {
    return true;
  }

  return false;
}

function shouldIgnoreError(error, hint) {
  return isProbablyBlockedByAdblockNetworkError(error) ||
    isThirdPartyBrowserError(error) ||
    isThirdPartyAxiosError(hint) ||
    isAnonymousCallStackExceeded(error) ||
    isAnonymousClickError(error);
}

Sentry.init({
  dsn: window.SENTRY_DSN_FRONTEND,
  environment: window.SENTRY_ENVIRONMENT,
  release: window.SENTRY_RELEASE,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 1.0,
  debug: window.SENTRY_ENVIRONMENT === 'development',
  beforeSend: (event, hint) => {
    return shouldIgnoreError(event, hint) ? null : event;
  },
  // This is a list of URLs that we want to allow to be sent to Sentry to prevent false positives
  allowUrls: [/^https?:\/\/((prod-cdn|staging-cdn|www)\.)?laundryheap\.com/],
  denyUrls: [
    /^blob:/i, // Ignore third-party injected scripts and web workers using Blob URLs
  ],
  ignoreErrors: [
    /Non-Error promise rejection/,
    /Object captured as promise rejection with keys: \[object has no keys\]/,
    /Fetch globalNotice timeout/,
    // Safari + browser extensions fire synthetic unhandledrejection CustomEvents with no stack trace
    /CustomEvent.*type=unhandledrejection/,
    // Facebook and third-party browser errors
    /Java object is gone/,
    /Script error/,
    /Network request failed/,
    /Load failed/,
    /The operation couldn't be completed/,
    /Non-Error promise rejection captured/,
    // WebView specific errors
    /WebView/i,
    // iOS WebKit message handler errors
    /window\.webkit\.messageHandlers/,
    /undefined is not an object \(evaluating 'window\.webkit\.messageHandlers/,
    /webkitExitFullScreen/,
    // Autofill callback handler errors
    /Can't find variable: _AutofillCallbackHandler/,
    /Can't find variable: gmo/,
    /Can't find variable: zaloJSV2/,
    /_AutofillCallbackHandler/,
    // WKWebView host app injecting scripts with typos (e.g. 'ifameElement' instead of 'iframeElement')
    /ifameElement\.contentDocument/,
    // Chrome WebView Java bridge errors on Android (e.g. Samsung foldables with occludeRectsOnNextFrame)
    /Java bridge method invocation error/,
    /occludeRectsOnNextFrame/,
    // DuckDuckGo Mobile browser internal privacy feature errors — not from our code
    /feature named .* was not found/,
    // SyntaxErrors caused by JS requests returning HTML (transient CDN/proxy issues)
    /Unexpected token '<'/,
    /No identifiers allowed directly after numeric literal/,
    /illegal character U\+009E/,
    /illegal character U\+003C/,
    // Network/Loading errors
    /Stylesheet append timeout/,
    // Old Safari/iOS missing Web Animations API (Element.animate) often triggered by GTM scripts
    /undefined is not a function \(near '\.\.\.a\.animate\.\.\.'\)/,
    // Meta Pixel (fbq) blocked by ad blockers — GTM fires tags calling fbq() before fbevents.js loads
    /Can't find variable: fbq/,
    /fbq is not defined/,
    // Google Analytics (gtag) blocked by ad blockers — CookieYes/GTM fire before gtag loads
    /gtag is not defined/,
    /Can't find variable: gtag/,
    // Usercentrics CMP (UC_UI) blocked by ad blockers — SDK injects onclick handlers that reference UC_UI
    /UC_UI is not defined/,
    /Can't find variable: UC_UI/,
    // Browser extension errors
    /Invalid call to runtime\.sendMessage/,
    /Tab not found/,
    // Third-party service errors
    /TrackerStorageType is not defined/,
    /globalThis.*is not defined/,
    // Obfuscated third-party unhandledrejection errors (no stack trace, likely Google Maps/reCAPTCHA or extensions)
    /^Yd$/,
    /^Zd$/,
    // Sentry internal errors
    /Cannot set property message of.*which has only a getter/,
    /An event processor returned `null`, will not send event/,
    // Browser security/storage errors
    /Failed to read the 'localStorage' property from 'Window': Access is denied/,
    /invalid origin/,
  ],
});

window.Sentry = Sentry;
